import React, { useState, useEffect } from 'react';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { User, Calendar as CalIcon, Clock, CheckCircle, ShoppingCart, MessageSquare, Shield, FolderLock } from 'lucide-react';
import MessageBoard from '../Messaging/MessageBoard';
import FileVault from '../Storage/FileVault';

const ClientDashboard: React.FC = () => {
  const { user, assignedLawyerId } = useAuth();
  const [activeTab, setActiveTab] = useState<'lawyer' | 'shop' | 'files'>('lawyer');
  const [cart, setCart] = useState<any[]>([]);
  const [lawyerInfo, setLawyerInfo] = useState<any>(null);

  const services = [
    { id: 'initial-consult', name: 'Initial Consultation', price: 150, desc: '30-minute introductory meeting with a legal expert.' },
    { id: 'doc-review', name: 'Document Review', price: 300, desc: 'Professional review and feedback on your legal documents.' },
    { id: 'incorporation', name: 'Business Incorporation', price: 800, desc: 'Full legal assistance with setting up your business entity.' }
  ];

  useEffect(() => {
    const fetchLawyerInfo = async () => {
      if (assignedLawyerId) {
        const snap = await getDoc(doc(db, 'users', assignedLawyerId));
        if (snap.exists()) setLawyerInfo(snap.data());
      }
    };
    fetchLawyerInfo();
  }, [assignedLawyerId]);

  const addToCart = (service: any) => {
    setCart([...cart, service]);
    alert(`${service.name} added to cart!`);
  };

  const checkout = () => {
    alert('Proceeding to secure payment gateway...');
    setCart([]);
  };

  return (
    <div className="container" style={{ padding: '40px 0' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
        <h1 style={{ color: '#0f172a' }}>Client Portal</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button onClick={() => setActiveTab('lawyer')} className={`btn ${activeTab === 'lawyer' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd' }}>My Lawyer</button>
          <button onClick={() => setActiveTab('files')} className={`btn ${activeTab === 'files' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd' }}>Secure Files</button>
          <button onClick={() => setActiveTab('shop')} className={`btn ${activeTab === 'shop' ? 'btn-primary' : ''}`} style={{ border: '1px solid #ddd' }}>Legal Services</button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: activeTab === 'shop' ? '2fr 1fr' : '1fr', gap: '30px' }}>
        
        {activeTab === 'lawyer' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '30px' }}>
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: 'var(--shadow)' }}>
              <h3 style={{ marginBottom: '20px' }}>Your Advisor</h3>
              {assignedLawyerId ? (
                <div style={{ textAlign: 'center' }}>
                  <div style={{ backgroundColor: '#f0f0f0', width: '80px', height: '80px', borderRadius: '50%', margin: '0 auto 15px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <User size={40} color="var(--primary-color)" />
                  </div>
                  <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{lawyerInfo?.email || 'Assigned Lawyer'}</div>
                  <p style={{ color: '#666', fontSize: '0.85rem', marginTop: '10px' }}>Available for consultation. Book via the messages board.</p>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '20px', backgroundColor: '#fff9eb', borderRadius: '8px', border: '1px solid #ffe58f' }}>
                  <p style={{ fontSize: '0.9rem' }}>Awaiting lawyer assignment. Our team will contact you shortly.</p>
                </div>
              )}
            </div>
            
            <div>
              {assignedLawyerId ? (
                <MessageBoard clientId={user!.uid} lawyerId={assignedLawyerId} />
              ) : (
                <div style={{ height: '400px', backgroundColor: '#f9f9f9', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', border: '2px dashed #eee' }}>
                  Communication will be enabled once a lawyer is assigned to your case.
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'files' && (
          <div style={{ maxWidth: '800px', margin: '0 auto', width: '100%' }}>
            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#e3f2fd', borderRadius: '8px', border: '1px solid #90caf9', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <FolderLock size={20} color="#1565c0" />
              <p style={{ fontSize: '0.9rem', color: '#1565c0' }}>Your files are encrypted and only accessible by you and your assigned legal advisor.</p>
            </div>
            <FileVault />
          </div>
        )}

        {activeTab === 'shop' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '20px' }}>
              {services.map(s => (
                <div key={s.id} style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: 'var(--shadow)', border: '1px solid #f0f0f0' }}>
                  <h3 style={{ marginBottom: '10px', color: 'var(--primary-color)' }}>{s.name}</h3>
                  <div style={{ fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '15px' }}>${s.price}</div>
                  <p style={{ color: '#666', fontSize: '0.85rem', marginBottom: '20px', minHeight: '60px' }}>{s.desc}</p>
                  <button onClick={() => addToCart(s)} className="btn btn-secondary" style={{ width: '100%' }}>Add to Cart</button>
                </div>
              ))}
            </div>
            
            <div style={{ backgroundColor: 'white', padding: '25px', borderRadius: '12px', boxShadow: 'var(--shadow)', height: 'fit-content' }}>
              <h3 style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <ShoppingCart size={20} /> Shopping Cart
              </h3>
              {cart.length === 0 ? (
                <p style={{ color: '#999', fontSize: '0.9rem' }}>Your cart is empty.</p>
              ) : (
                <>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
                    {cart.map((item, idx) => (
                      <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                        <span>{item.name}</span>
                        <span>${item.price}</span>
                      </div>
                    ))}
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '10px', marginTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold' }}>
                      <span>Total</span>
                      <span>${cart.reduce((sum, item) => sum + item.price, 0)}</span>
                    </div>
                  </div>
                  <button onClick={checkout} className="btn btn-primary" style={{ width: '100%' }}>Checkout</button>
                </>
              )}
            </div>
          </>
        )}

      </div>
    </div>
  );
};

export default ClientDashboard;
